import { Box, Stack } from "@mui/material";
import { useModalState } from "@src/lib/ui-react";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { useEffect } from "react";

import { LogoutConfirmationDialog } from "./LogoutConfirmationDialog";
import { Title } from "@src/appV2/redesign/components/Title";

const underConstructionImage = `${import.meta.env.BASE_URL}assets/under-construction.png`;

/**
 * Dead-end landing page for Home Health workplace users who hit the app on
 * mobile (native Capacitor app or narrow viewport). HH workplace surfaces
 * (Cases, Visit Verification, Coverage Map) are not designed for mobile yet,
 * so we block all in-app navigation and present a single Logout action.
 *
 * Rendered directly by `NavigationMenuContainer` outside the app layout
 * chrome so no sidebar/nav drawer is reachable.
 */
export function HomeHealthMobileUnsupportedPage() {
  const logoutDialog = useModalState();

  useEffect(() => {
    logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_MOBILE_UNSUPPORTED_VIEWED);
  }, []);

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background?.default,
        paddingX: 5,
        paddingTop: "max(24px, env(safe-area-inset-top))",
        paddingBottom: "max(20px, env(safe-area-inset-bottom))",
      })}
    >
      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <ClipboardLogo height={30} width={100} />
      </Box>

      <Stack
        spacing={3}
        sx={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          maxWidth: 480,
          marginX: "auto",
        }}
      >
        <Box
          component="img"
          src={underConstructionImage}
          alt=""
          sx={{ width: "100%", maxWidth: 150, height: "auto" }}
        />
        <Title component="h5" variant="h5">
          Under construction
        </Title>
        <Text variant="h6" sx={(theme) => ({ color: theme.palette.text?.secondary })}>
          We&apos;re currently making improvements to our mobile app. When it&apos;s ready,
          we&apos;ll let you know!
        </Text>
      </Stack>

      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <Button variant="outlined" onClick={logoutDialog.openModal}>
          Log Out
        </Button>
      </Box>

      <LogoutConfirmationDialog modalState={logoutDialog} />
    </Box>
  );
}
